import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ShieldCheck, UserX, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';
import './BarAdmissionsManager.css';

const BarAdmissionsManager = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('isApproved', '==', false));
      const snap = await getDocs(q);

      const list = [];
      snap.forEach(docSnap => {
        const data = docSnap.data();
        list.push({
          id: docSnap.id,
          ...data
        });
      });
      setPendingUsers(list);
    } catch (err) {
      console.error('Error fetching pending users:', err);
      toast.error('Failed to load pending admissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const handleDecision = async (userId, approve) => {
    try {
      setProcessingId(userId);
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        isApproved: approve,
        status: approve ? 'approved' : 'rejected',
        approvedAt: serverTimestamp()
      });

      toast.success(approve ? 'Juror admitted to the Bar!' : 'Application rejected.');
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      console.error('Error deciding admission:', err);
      toast.error('Failed to update admission status.');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return <div className="admissions-loading">Loading pending applications...</div>;
  }

  return (
    <div className="bar-admissions-container">
      <div className="admissions-header">
        <h2>
          <ShieldCheck className="header-icon" />
          Bar Admissions & Peer Review
        </h2>
        <p>Review and admit friends to access The Kangaroo Court, Docket, and Vault of Shame.</p>
      </div>

      {pendingUsers.length === 0 ? (
        <div className="admissions-empty">
          <CheckCircle2 size={40} className="empty-icon" />
          <h3>All Clear, Chief Justice!</h3>
          <p>No applications currently pending review. All network members have been admitted.</p>
        </div>
      ) : (
        <div className="admissions-list">
          {pendingUsers.map(user => (
            <div key={user.id} className="admission-card">
              <div className="applicant-avatar">
                {user.photoURL ? (
                  <img src={user.photoURL} alt={user.name} />
                ) : (
                  <div className="avatar-placeholder">{user.name?.charAt(0) || 'J'}</div>
                )}
              </div>
              <div className="applicant-info">
                <h4>{user.name || 'Unnamed Juror'}</h4>
                <span className="applicant-email">{user.email}</span>
                <span className="applicant-discipline">
                  Field: <strong>{user.profession || 'General Professional'}</strong>
                </span>
              </div>
              <div className="admission-actions">
                <button
                  className="btn-admit"
                  disabled={processingId === user.id}
                  onClick={() => handleDecision(user.id, true)}
                >
                  <ShieldCheck size={16} />
                  Admit to Bar
                </button>
                <button
                  className="btn-reject"
                  disabled={processingId === user.id}
                  onClick={() => handleDecision(user.id, false)}
                >
                  <UserX size={16} />
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BarAdmissionsManager;
